import type { PlacementPhoto } from "@/types/inertia"

/**
 * The grounds the plates cycle through, one per row. Five colours rather than
 * one because a column of identical squares reads as a single block and a
 * column of different ones reads as a list of places — which is the whole
 * point of the index. All five are the brand's own; the two pale ones take
 * navy lettering instead of white.
 *
 * The wash sits in the same range as the page heroes (0.78–0.86): enough that
 * the name reads over any photograph, little enough that the room underneath
 * is still a room. The two pale grounds carry more, because navy lettering on
 * a light wash needs the picture quieter than white lettering does.
 */
const PLATES = [
  { wash: "rgba(95,116,147,0.78)",  ink: "#FFFFFF" }, // navy
  { wash: "rgba(21,40,68,0.74)",    ink: "#FFFFFF" }, // the films' deep blue
  { wash: "rgba(134,155,190,0.82)", ink: "#FFFFFF" }, // navy-mid
  { wash: "rgba(180,202,186,0.86)", ink: "#152844" }, // sage
  { wash: "rgba(232,224,214,0.86)", ink: "#152844" }, // stone-mid
]

/**
 * One square on the therapies index: a room of hers, a colour over it, and the
 * therapy's short name — CBT, EMDR, I-CBT — in her display face. The type is
 * set, never drawn into the photograph, so it stays crisp at any size and can
 * be changed in a word.
 *
 * The whole plate is aria-hidden: the row prints the therapy's full name in
 * its heading immediately beside this, and a screen reader should hear that
 * once. With no photo placed the wash is the whole ground, which still reads.
 */
export function TherapyPlate({ photo, name, fullName, index }: {
  photo?: PlacementPhoto | null
  /** The short name — "CBT" out of "Cognitive Behavioural Therapy (CBT)". */
  name: string
  /** The heading it came out of, for the line under the rule. */
  fullName: string
  /** Position in the list; decides which of the five grounds it takes. */
  index: number
}) {
  const { wash, ink } = PLATES[index % PLATES.length]

  // The line under the rule is the name with its own abbreviation taken back
  // out — "Cognitive Behavioural Therapy", not "…Therapy (CBT)" under a plate
  // that already says CBT. A therapy whose name has no abbreviation in it
  // (EMDR) has nothing left to say here and shows the acronym alone. It is
  // held back below sm as well: the square is 88px on a phone, and eight-point
  // capitals in it are a texture rather than a word.
  const subtitle = name === fullName ? null : fullName.replace(/\s*\([^()]*\)\s*$/, "")

  return (
    <span aria-hidden className="relative block aspect-square w-full overflow-hidden rounded-2xl ring-1 ring-hairline">
      {photo?.card_url && (
        <img
          src={photo.card_url}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          decoding="async"
        />
      )}
      <span className="absolute inset-0" style={{ background: wash }} />
      <span className="relative flex h-full flex-col items-center justify-center gap-1.5 px-1.5 sm:gap-2 sm:px-4" style={{ color: ink }}>
        <span className="font-display text-[1.05rem] leading-none sm:text-[1.7rem]">{name}</span>
        <span className="h-px w-5 sm:w-7" style={{ background: ink, opacity: 0.5 }} />
        {subtitle && (
          <span
            className="hidden text-[0.5rem] font-medium uppercase leading-tight tracking-[0.1em] sm:block"
            style={{ opacity: 0.8 }}
          >
            {subtitle}
          </span>
        )}
      </span>
    </span>
  )
}
